import React, { useState, useEffect, useCallback } from 'react';
import { 
    Plus, FolderOpen, Pencil, Trash2, X, ClipboardPlus, Calendar, 
    User, Stethoscope, FileText, Check, AlertTriangle, ListFilter
} from 'lucide-react';
// import { initializeApp } from 'firebase/app'; // Eliminado
// import { getAuth, signInAnonymously, signInWithCustomToken, onAuthStateChanged } from 'firebase/auth'; // Eliminado
// import { getFirestore, doc, addDoc, setDoc, updateDoc, deleteDoc, onSnapshot, collection } from 'firebase/firestore'; // Eliminado

// NOTA IMPORTANTE: Esta versión usa datos en memoria (useState).
// Los datos se perderán al recargar la página, pero es funcional
// y elimina las dependencias externas (Firebase/localStorage).

// Estructura de datos inicial
const initialExpedientesData = [
    {
        id: 1,
        name: 'Ana García',
        patientId: 'P0001',
        dob: '1990-05-15',
        gender: 'Femenino',
        allergies: 'Penicilina, Polen',
        chronicDiseases: 'Asma (controlada)',
        pastSurgeries: 'Apéndice (2010)',
        consultationNotes: [
            { id: 101, date: '2025-11-01', reason: 'Fiebre y tos', diagnosis: 'Resfriado común', treatment: 'Paracetamol y reposo' },
        ]
    },
    {
        id: 2,
        name: 'Roberto Pérez',
        patientId: 'P0002',
        dob: '1985-10-25',
        gender: 'Masculino',
        allergies: 'Ninguna conocida',
        chronicDiseases: 'Hipertensión',
        pastSurgeries: 'Ninguna',
        consultationNotes: [
            { id: 201, date: '2025-11-21', reason: 'Chequeo de presión', diagnosis: 'Presión estable', treatment: 'Ajuste de dosis de Losartán' },
        ]
    }
];

const initialRecord = {
    id: null,
    name: '',
    patientId: '',
    dob: '',
    gender: 'Masculino', // Valor por defecto
    allergies: '',
    chronicDiseases: '',
    pastSurgeries: '',
    consultationNotes: [],
};

export default function ExpedientesView() {
    // Inicialización simple en memoria.
    const [records, setRecords] = useState(initialExpedientesData);

    // No hay useEffect para localStorage ni Firebase.
    // La persistencia externa deberá ser implementada aquí con tu propia API.

    // Estados para la Interfaz
    const [message, setMessage] = useState('');
    const [isModalOpen, setIsModalOpen] = useState(false); // Modal principal para crear/editar paciente
    const [isNotesModalOpen, setIsNotesModalOpen] = useState(false); // Modal para añadir nota
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false); // Nuevo modal para confirmar eliminación
    const [recordToDelete, setRecordToDelete] = useState(null); // Registro a eliminar
    const [currentRecord, setCurrentRecord] = useState(initialRecord);
    const [newNote, setNewNote] = useState({ date: '', reason: '', diagnosis: '', treatment: '' });

    const showMessage = useCallback((msg, type = 'success') => {
        setMessage({ msg, type });
        setTimeout(() => setMessage(''), 3000);
    }, []);

    // --- Funciones de Expedientes (CRUD principal) ---

    const handleCreateRecord = () => {
        // Al crear, simplemente reseteamos el registro actual a su estado inicial.
        setCurrentRecord(initialRecord);
        setIsModalOpen(true);
    };

    const handleEditRecord = (record) => {
        // Clonar el registro para editarlo
        setCurrentRecord({ ...record }); 
        setIsModalOpen(true);
    };
    
    const handleSaveRecord = (e) => {
        e.preventDefault();

        // En una aplicación real, aquí llamarías a tu API para GUARDAR o ACTUALIZAR.

        if (currentRecord.id) {
            // CASO: ACTUALIZAR
            setRecords(records.map(r => r.id === currentRecord.id ? currentRecord : r));
            showMessage(`[ACTUALIZADO] Expediente de ${currentRecord.name} modificado con éxito.`, 'success');
        } else {
            // CASO: CREAR (Generación de ID local, solo para demostración en memoria)
            const maxId = records.length > 0 ? Math.max(...records.map(r => r.id)) : 0;
            const newId = maxId + 1;
            const newPatientId = 'P' + String(newId).padStart(4, '0');
            
            setRecords([...records, { ...currentRecord, id: newId, patientId: newPatientId }]);
            showMessage(`[CREADO] Expediente para ${currentRecord.name} creado con éxito.`, 'success');
        }

        setIsModalOpen(false);
        setCurrentRecord(initialRecord);
    };

    const handleRecordFormChange = (e) => {
        const { name, value } = e.target;
        setCurrentRecord({ ...currentRecord, [name]: value });
    };

    // --- Funciones de Eliminación (con Modal de Confirmación) ---

    const handleConfirmDeleteClick = (record) => {
        setRecordToDelete(record);
        setIsDeleteModalOpen(true);
    };

    const handleExecuteDelete = () => {
        if (!recordToDelete) return;
        
        // En una aplicación real, aquí llamarías a tu API para ELIMINAR.

        setRecords(records.filter(r => r.id !== recordToDelete.id));
        showMessage("Expediente eliminado.", 'success');
        setIsDeleteModalOpen(false);
        setRecordToDelete(null);
    };

    // --- Funciones de Notas de Consulta ---

    const handleAddNoteClick = (record) => {
        // Clonar el registro para manejarlo en el modal de notas
        setCurrentRecord({ ...record }); 
        const today = new Date().toISOString().substring(0, 10);
        setNewNote({ date: today, reason: '', diagnosis: '', treatment: '' });
        setIsNotesModalOpen(true);
    };

    const handleNoteFormChange = (e) => {
        const { name, value } = e.target;
        setNewNote({ ...newNote, [name]: value });
    };

    const handleSaveNote = (e) => {
        e.preventDefault();

        if (!currentRecord.id) return;

        // En una aplicación real, aquí llamarías a tu API para AÑADIR la nota.

        const newNoteId = currentRecord.consultationNotes.length > 0
            ? Math.max(...currentRecord.consultationNotes.map(n => n.id)) + 1
            : 1;

        const updatedRecord = {
            ...currentRecord,
            consultationNotes: [
                ...currentRecord.consultationNotes,
                { ...newNote, id: newNoteId }
            ].sort((a, b) => new Date(b.date) - new Date(a.date)) // Ordenar por fecha descendente
        };

        // Actualizar el estado global de expedientes
        setRecords(records.map(r => r.id === currentRecord.id ? updatedRecord : r));
        
        showMessage(`[NOTA AÑADIDA] Nueva consulta registrada para ${currentRecord.name}.`, 'success');
        setIsNotesModalOpen(false);
        setCurrentRecord(updatedRecord); // Mantener el registro actualizado en la vista
    };
    
    // --- Renderizado principal ---

    return (
        <div className="p-4 md:p-8 max-w-7xl mx-auto font-sans min-h-screen">
            {/* Notificación Visual Flotante */}
            {message && (
                <div className="fixed top-0 left-1/2 transform -translate-x-1/2 mt-4 z-50">
                    <div className={`${message.type === 'success' ? 'bg-green-500' : (message.type === 'info' ? 'bg-blue-500' : 'bg-red-500')} text-white font-bold py-2 px-4 rounded-lg shadow-xl transition duration-500 flex items-center space-x-2`}>
                        {message.type === 'success' ? <Check className="w-5 h-5" /> : <AlertTriangle className="w-5 h-5" />}
                        <span>{message.msg}</span>
                    </div>
                </div>
            )}

            <h1 className="text-3xl font-extrabold text-gray-900 mb-6 border-b pb-2 flex items-center space-x-2">
                <FolderOpen className="w-8 h-8 text-indigo-600" />
                <span>Gestión de Expedientes Médicos</span>
            </h1>

            
            {/* Botón para crear */}
            <div className="mb-6">
                <button
                    onClick={handleCreateRecord}
                    className="flex items-center px-4 py-2 bg-indigo-600 text-white font-medium rounded-lg shadow-md hover:bg-indigo-700 transition duration-150 transform hover:scale-[1.01]"
                >
                    <Plus className="h-5 w-5 mr-2" />
                    Nuevo Expediente
                </button>
            </div>

            <h2 className="text-xl font-semibold text-gray-700 mb-4 flex items-center space-x-2">
                <ListFilter className="w-5 h-5 text-gray-500" />
                <span>Pacientes Registrados ({records.length})</span>
            </h2>

            {/* Listado de Expedientes/Pacientes */}
            <div className="space-y-4">
                {records.length === 0 && (
                    <p className="text-gray-500 italic p-4 bg-gray-50 rounded-lg border">No hay expedientes registrados. Crea uno nuevo para empezar.</p>
                )}

                {records.map((record) => (
                    <div key={record.id} className="bg-white p-6 rounded-xl shadow-lg border border-gray-100 transition duration-300 hover:shadow-xl">
                        <div className="flex justify-between items-center mb-4 border-b pb-2">
                            <h3 className="text-xl font-bold text-indigo-700 flex items-center space-x-2">
                                <User className="w-5 h-5" />
                                <span>{record.name}</span>
                            </h3>
                            <div className="text-sm font-medium bg-indigo-100 text-indigo-800 px-3 py-1 rounded-full flex items-center space-x-1">
                                <FileText className="w-4 h-4" />
                                <span>ID: {record.patientId || 'PENDIENTE'}</span>
                            </div>
                        </div>

                        {/* Detalles clave del paciente */}
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm text-gray-700 mb-4">
                            <div className="flex items-center space-x-1">
                                <Calendar className="w-4 h-4 text-gray-500" />
                                <strong>Nacimiento:</strong> {record.dob}
                            </div>
                            <div className="flex items-center space-x-1">
                                <User className="w-4 h-4 text-gray-500" />
                                <strong>Género:</strong> {record.gender}
                            </div>
                            <div className="col-span-2 flex items-start">
                                <Stethoscope className="w-4 h-4 mt-0.5 mr-2 text-gray-500 flex-shrink-0" />
                                <span className="font-bold">Alergias:</span> {record.allergies || 'Ninguna'}
                            </div>
                            <div className="col-span-2 flex items-start">
                                <Stethoscope className="w-4 h-4 mt-0.5 mr-2 text-gray-500 flex-shrink-0" />
                                <span className="font-bold">Crónicas:</span> {record.chronicDiseases || 'Ninguna'}
                            </div>
                            <div className="col-span-4 flex items-start">
                                <Stethoscope className="w-4 h-4 mt-0.5 mr-2 text-gray-500 flex-shrink-0" />
                                <span className="font-bold">Cirugías Previas:</span> {record.pastSurgeries || 'Ninguna'}
                            </div>
                        </div>
                        
                        {/* Historial de Consultas */}
                        <div className="mt-4 pt-4 border-t border-gray-200">
                            <h4 className="text-lg font-semibold mb-2 text-gray-800 flex items-center space-x-2">
                                <FileText className="w-5 h-5 text-gray-600" />
                                <span>Historial de Consultas ({record.consultationNotes.length})</span>
                            </h4>
                            
                            <div className="space-y-3 max-h-48 overflow-y-auto pr-2">
                                {record.consultationNotes.length === 0 ? (
                                    <p className="italic text-gray-500 text-sm">No hay notas de consulta para este paciente.</p>
                                ) : (
                                    record.consultationNotes
                                        .slice() // Copia para ordenar y no modificar el original
                                        .sort((a, b) => new Date(b.date) - new Date(a.date)) // Ordenar por fecha descendente
                                        .map((note) => (
                                            <div key={note.id} className="p-3 bg-gray-50 rounded-lg border border-gray-200">
                                                <div className="flex justify-between text-xs font-medium text-gray-600 mb-1">
                                                    <span className="flex items-center space-x-1">
                                                        <Calendar className="w-3 h-3" />
                                                        <span>Fecha: {note.date}</span>
                                                    </span>
                                                    <span className="text-indigo-600">Motivo: {note.reason}</span>
                                                </div>
                                                <p className="text-sm text-gray-700">
                                                    <span className="font-semibold">Diagnóstico:</span> {note.diagnosis}
                                                </p>
                                                <p className="text-sm text-gray-700">
                                                    <span className="font-semibold">Tratamiento:</span> {note.treatment}
                                                </p>
                                            </div>
                                        ))
                                )}
                            </div>
                        </div>

                        {/* Botones de acción del expediente */}
                        <div className="flex justify-end space-x-3 mt-4 pt-4 border-t border-gray-200">
                            <button
                                onClick={() => handleAddNoteClick(record)}
                                className="flex items-center px-3 py-1 text-sm bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition duration-150"
                            >
                                <ClipboardPlus className="w-4 h-4 mr-1" />
                                Añadir Nota
                            </button>
                            <button
                                onClick={() => handleEditRecord(record)}
                                className="flex items-center px-3 py-1 text-sm bg-yellow-500 text-white rounded-lg hover:bg-yellow-600 transition duration-150"
                            >
                                <Pencil className="w-4 h-4 mr-1" />
                                Editar Expediente
                            </button>
                            <button
                                onClick={() => handleConfirmDeleteClick(record)}
                                className="flex items-center px-3 py-1 text-sm bg-red-500 text-white rounded-lg hover:bg-red-600 transition duration-150"
                            >
                                <Trash2 className="w-4 h-4 mr-1" />
                                Eliminar
                            </button>
                        </div>
                    </div>
                ))}
            </div>

            {/* MODAL de CREAR/EDITAR Expediente */}
            {isModalOpen && (
                <div className="fixed inset-0 bg-gray-600 bg-opacity-75 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-xl shadow-2xl w-full max-w-3xl p-6 max-h-[90vh] overflow-y-auto">
                        <div className="flex justify-between items-center border-b pb-3 mb-4">
                            <h3 className="text-2xl font-bold text-gray-900 flex items-center space-x-2">
                                <FolderOpen className="w-6 h-6 text-indigo-600" />
                                <span>{currentRecord.id ? `Editar Expediente #${currentRecord.patientId}` : 'Crear Nuevo Expediente'}</span>
                            </h3>
                            <button onClick={() => setIsModalOpen(false)} className="text-gray-400 hover:text-gray-600 p-1 rounded-full hover:bg-gray-100 transition">
                                <X className="w-6 h-6" />
                            </button>
                        </div>
                        
                        <form onSubmit={handleSaveRecord}>
                            
                            <fieldset className="p-4 border rounded-lg mb-6 shadow-sm">
                                <legend className="px-2 text-lg font-semibold text-indigo-700 flex items-center space-x-1">
                                    <User className="w-5 h-5" />
                                    <span>Datos Personales</span>
                                </legend>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <label className="block">
                                        <span className="text-gray-700">Nombre Completo:</span>
                                        <input type="text" name="name" required value={currentRecord.name}
                                            onChange={handleRecordFormChange}
                                            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm p-2 focus:ring-indigo-500 focus:border-indigo-500"
                                            placeholder="Ej: Juan Antonio López"
                                        />
                                    </label>
                                    <label className="block">
                                        <span className="text-gray-700">Identificación (ID):</span>
                                        <input type="text" name="patientId" required 
                                            value={currentRecord.patientId || 'SE ASIGNARÁ AL GUARDAR'}
                                            onChange={handleRecordFormChange} readOnly={!!currentRecord.id}
                                            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm p-2 bg-gray-100"
                                        />
                                    </label>
                                    <label className="block">
                                        <span className="text-gray-700">Fecha de Nacimiento:</span>
                                        <input type="date" name="dob" required value={currentRecord.dob}
                                            onChange={handleRecordFormChange}
                                            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm p-2 focus:ring-indigo-500 focus:border-indigo-500"
                                        />
                                    </label>
                                    <label className="block">
                                        <span className="text-gray-700">Género:</span>
                                        <select name="gender" required value={currentRecord.gender}
                                            onChange={handleRecordFormChange}
                                            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm p-2 focus:ring-indigo-500 focus:border-indigo-500"
                                        >
                                            <option value="Masculino">Masculino</option>
                                            <option value="Femenino">Femenino</option>
                                            <option value="Otro">Otro</option>
                                        </select>
                                    </label>
                                </div>
                            </fieldset>

                            <fieldset className="p-4 border rounded-lg mb-6 shadow-sm">
                                <legend className="px-2 text-lg font-semibold text-indigo-700 flex items-center space-x-1">
                                    <Stethoscope className="w-5 h-5" />
                                    <span>Historial Médico Base</span>
                                </legend>
                                <label className="block mb-4">
                                    <span className="text-gray-700">Alergias (Separadas por coma):</span>
                                    <textarea name="allergies" rows="2" value={currentRecord.allergies}
                                        onChange={handleRecordFormChange}
                                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm p-2 focus:ring-indigo-500 focus:border-indigo-500"
                                        placeholder="Ej: Penicilina, Látex, Alimentos"
                                    />
                                </label>
                                <label className="block mb-4">
                                    <span className="text-gray-700">Enfermedades Crónicas:</span>
                                    <textarea name="chronicDiseases" rows="2" value={currentRecord.chronicDiseases}
                                        onChange={handleRecordFormChange}
                                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm p-2 focus:ring-indigo-500 focus:border-indigo-500"
                                        placeholder="Ej: Diabetes Tipo 2, Hipotiroidismo"
                                    />
                                </label>
                                <label className="block">
                                    <span className="text-gray-700">Cirugías Previas / Hospitalizaciones:</span>
                                    <textarea name="pastSurgeries" rows="2" value={currentRecord.pastSurgeries}
                                        onChange={handleRecordFormChange}
                                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm p-2 focus:ring-indigo-500 focus:border-indigo-500"
                                        placeholder="Ej: Cesárea (2018), Fractura de fémur (2005)"
                                    />
                                </label>
                            </fieldset>

                            {/* Botones de acción */}
                            <div className="flex justify-end space-x-4 pt-4 border-t">
                                <button type="button" onClick={() => setIsModalOpen(false)}
                                    className="flex items-center bg-gray-300 text-gray-800 font-bold py-2 px-4 rounded-lg hover:bg-gray-400 transition duration-150">
                                    <X className="w-5 h-5 mr-2" />
                                    Cancelar
                                </button>
                                <button type="submit"
                                    className="flex items-center bg-indigo-600 text-white font-bold py-2 px-4 rounded-lg hover:bg-indigo-700 transition duration-150">
                                    <Check className="w-5 h-5 mr-2" />
                                    {currentRecord.id ? 'Guardar Cambios' : 'Crear Expediente'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
            
            {/* MODAL de AÑADIR NOTA DE CONSULTA */}
            {isNotesModalOpen && (
                <div className="fixed inset-0 bg-gray-600 bg-opacity-75 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-xl shadow-2xl w-full max-w-xl p-6">
                        <div className="flex justify-between items-center border-b pb-3 mb-4">
                            <h3 className="text-2xl font-bold text-gray-900 flex items-center space-x-2">
                                <ClipboardPlus className="w-6 h-6 text-blue-600" />
                                <span>Nueva Nota de Consulta para {currentRecord.name}</span>
                            </h3>
                            <button onClick={() => setIsNotesModalOpen(false)} className="text-gray-400 hover:text-gray-600 p-1 rounded-full hover:bg-gray-100 transition">
                                <X className="w-6 h-6" />
                            </button>
                        </div>
                        
                        <form onSubmit={handleSaveNote}>
                            <div className="grid grid-cols-2 gap-4 mb-4">
                                <label className="block">
                                    <span className="text-gray-700 flex items-center space-x-1"><Calendar className="w-4 h-4" />Fecha de Consulta:</span>
                                    <input type="date" name="date" required value={newNote.date}
                                        onChange={handleNoteFormChange}
                                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm p-2 focus:ring-blue-500 focus:border-blue-500"
                                    />
                                </label>
                                <label className="block">
                                    <span className="text-gray-700 flex items-center space-x-1"><FileText className="w-4 h-4" />Motivo de Consulta:</span>
                                    <input type="text" name="reason" required value={newNote.reason}
                                        onChange={handleNoteFormChange}
                                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm p-2 focus:ring-blue-500 focus:border-blue-500"
                                        placeholder="Ej: Dolor de cabeza, Chequeo"
                                    />
                                </label>
                            </div>

                            <label className="block mb-4">
                                <span className="text-gray-700 flex items-center space-x-1"><Stethoscope className="w-4 h-4" />Diagnóstico:</span>
                                <textarea name="diagnosis" rows="3" required value={newNote.diagnosis}
                                    onChange={handleNoteFormChange}
                                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm p-2 focus:ring-blue-500 focus:border-blue-500"
                                    placeholder="Ej: Migraña tensional. Se descartó infección viral."
                                />
                            </label>

                            <label className="block mb-6">
                                <span className="text-gray-700 flex items-center space-x-1"><Pencil className="w-4 h-4" />Tratamiento/Indicaciones:</span>
                                <textarea name="treatment" rows="3" required value={newNote.treatment}
                                    onChange={handleNoteFormChange}
                                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm p-2 focus:ring-blue-500 focus:border-blue-500"
                                    placeholder="Ej: Reposo 24h. Tomar 500mg de Paracetamol cada 8h por 3 días."
                                />
                            </label>

                            {/* Botones de acción */}
                            <div className="flex justify-end space-x-4 pt-4 border-t">
                                <button type="button" onClick={() => setIsNotesModalOpen(false)}
                                    className="flex items-center bg-gray-300 text-gray-800 font-bold py-2 px-4 rounded-lg hover:bg-gray-400 transition duration-150">
                                    <X className="w-5 h-5 mr-2" />
                                    Cancelar
                                </button>
                                <button type="submit"
                                    className="flex items-center bg-blue-600 text-white font-bold py-2 px-4 rounded-lg hover:bg-blue-700 transition duration-150">
                                    <Check className="w-5 h-5 mr-2" />
                                    Guardar Nota
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* MODAL de CONFIRMACIÓN de Eliminación */}
            {isDeleteModalOpen && recordToDelete && (
                <div className="fixed inset-0 bg-gray-600 bg-opacity-75 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-xl shadow-2xl w-full max-w-sm p-6">
                        <div className="flex justify-between items-center border-b pb-3 mb-4">
                            <h3 className="text-xl font-bold text-red-600 flex items-center space-x-2">
                                <AlertTriangle className="w-6 h-6" />
                                <span>Confirmar Eliminación</span>
                            </h3>
                            <button onClick={() => setIsDeleteModalOpen(false)} className="text-gray-400 hover:text-gray-600 p-1 rounded-full hover:bg-gray-100 transition">
                                <X className="w-6 h-6" />
                            </button>
                        </div>
                        
                        <p className="text-gray-700 mb-6">
                            ¿Está seguro de que desea <strong className="text-red-700">ELIMINAR</strong> permanentemente el expediente de 
                            <span className="font-semibold"> {recordToDelete.name}</span> (ID: {recordToDelete.patientId})? Esta acción no se puede deshacer.
                        </p>
                        
                        <div className="flex justify-end space-x-4 pt-4 border-t">
                            <button type="button" onClick={() => setIsDeleteModalOpen(false)}
                                className="flex items-center bg-gray-300 text-gray-800 font-bold py-2 px-4 rounded-lg hover:bg-gray-400 transition duration-150">
                                <X className="w-5 h-5 mr-2" />
                                Cancelar
                            </button>
                            <button type="button" onClick={handleExecuteDelete}
                                className="flex items-center bg-red-600 text-white font-bold py-2 px-4 rounded-lg hover:bg-red-700 transition duration-150">
                                <Trash2 className="w-5 h-5 mr-2" />
                                Sí, Eliminar
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}